import React, { FC, useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Button, Input, PopoverBody, UncontrolledPopover } from 'reactstrap';

import { VisibilityOption } from './types';

interface ColumnRowProps {
  col: {
    key: string;
    label: string;
    visibility: string;
    isNew?: boolean;
  };
  visibilityOptions: VisibilityOption[];
  otherColumnLabels?: Set<string>;
  onChange: (key: string, visibility: string) => void;
  onLabelChange?: (key: string, label: string) => void;
  onDelete?: (key: string) => void;
  deletingColumnKey?: string | null;
  hasDeleteCol: boolean;
}

const ColumnRow: FC<ColumnRowProps> = ({
  col,
  visibilityOptions,
  otherColumnLabels,
  onChange,
  onLabelChange,
  onDelete,
  deletingColumnKey,
  hasDeleteCol,
}) => {
  const [isConfirming, setIsConfirming] = useState(false);
  const isDeleting = deletingColumnKey === col.key;

  const handleTrashClick = useCallback(() => setIsConfirming(true), []);
  const handleCancelDelete = useCallback(() => setIsConfirming(false), []);
  const handleConfirmDelete = useCallback(() => {
    setIsConfirming(false);
    onDelete?.(col.key);
  }, [col.key, onDelete]);

  const handleLabelChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onLabelChange?.(col.key, e.target.value);
    },
    [col.key, onLabelChange]
  );

  const handleVisibilityChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(col.key, e.target.value);
    },
    [col.key, onChange]
  );

  const showLabelInput = !!onLabelChange && col.isNew !== false;
  const warningTargetId = `col-label-warn-${col.key.replace(
    /[^a-zA-Z0-9_-]/g,
    '_'
  )}`;
  const labelConflict =
    showLabelInput && !!otherColumnLabels?.has(col.label.toLowerCase().trim());

  if (isConfirming) {
    return (
      <tr>
        <td colSpan={hasDeleteCol ? 3 : 2} className="align-middle">
          <span className="me-3">
            <FormattedMessage
              id="app.custom_campaign_data.delete.confirm_message"
              defaultMessage="Are you sure? The operation cannot be undone."
            />
          </span>
          <Button
            color="link"
            size="sm"
            className="text-danger p-0 me-2"
            disabled={isDeleting}
            onClick={handleConfirmDelete}
          >
            {isDeleting ? (
              <span
                className="spinner-border spinner-border-sm"
                role="status"
              />
            ) : (
              <FormattedMessage
                id="app.custom_campaign_data.delete.yes"
                defaultMessage="Yes"
              />
            )}
          </Button>
          <Button
            color="link"
            size="sm"
            className="text-secondary p-0"
            onClick={handleCancelDelete}
          >
            <FormattedMessage
              id="app.custom_campaign_data.delete.no"
              defaultMessage="No"
            />
          </Button>
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <td>
        {showLabelInput ? (
          <div className="d-flex align-items-center gap-2">
            <Input
              type="text"
              bsSize="sm"
              value={col.label}
              onChange={handleLabelChange}
            />
            {labelConflict && (
              <>
                <i
                  id={warningTargetId}
                  className="fe fe-alert-triangle text-warning"
                  style={{ cursor: 'pointer', flexShrink: 0 }}
                />
                <UncontrolledPopover
                  trigger="hover focus"
                  target={warningTargetId}
                  placement="top"
                >
                  <PopoverBody>
                    <FormattedMessage
                      id="app.custom_campaign_data.permissions.label_conflict"
                      defaultMessage="This label is already used by a column owned by another user."
                    />
                  </PopoverBody>
                </UncontrolledPopover>
              </>
            )}
          </div>
        ) : (
          <>
            {col.label}
            {col.isNew && (
              <span className="badge bg-info ms-2">
                <FormattedMessage
                  id="app.custom_campaign_data.permissions.new_badge"
                  defaultMessage="new"
                />
              </span>
            )}
          </>
        )}
      </td>
      <td>
        <Input
          type="select"
          bsSize="sm"
          value={col.visibility}
          onChange={handleVisibilityChange}
        >
          {visibilityOptions.map((v) => (
            <option key={v.value} value={v.value}>
              {v.label}
            </option>
          ))}
        </Input>
      </td>
      {hasDeleteCol && (
        <td className="text-center">
          <Button
            color="link"
            size="sm"
            className="text-danger p-0"
            disabled={isDeleting}
            onClick={handleTrashClick}
          >
            {isDeleting ? (
              <span
                className="spinner-border spinner-border-sm"
                role="status"
              />
            ) : (
              <i className="fe fe-trash-2" />
            )}
          </Button>
        </td>
      )}
    </tr>
  );
};

interface ColumnVisibilityTableProps {
  columns: {
    key: string;
    label: string;
    visibility: string;
    isNew?: boolean;
  }[];
  visibilityOptions: VisibilityOption[];
  otherColumnLabels?: Set<string>;
  onChange: (key: string, visibility: string) => void;
  onLabelChange?: (key: string, label: string) => void;
  onDelete?: (key: string) => void;
  deletingColumnKey?: string | null;
}

const ColumnVisibilityTable: FC<ColumnVisibilityTableProps> = ({
  columns,
  visibilityOptions,
  otherColumnLabels,
  onChange,
  onLabelChange,
  onDelete,
  deletingColumnKey,
}) => (
  <div className="table-responsive mb-3">
    <table className="card-table table table-sm mb-0">
      <thead>
        <tr>
          <th>
            <div className="text-muted">
              <FormattedMessage
                id="app.custom_campaign_data.permissions.column"
                defaultMessage="Column"
              />
            </div>
          </th>
          <th>
            <div className="text-muted">
              <FormattedMessage
                id="app.custom_campaign_data.permissions.visibility"
                defaultMessage="Visibility"
              />
            </div>
          </th>
          {onDelete && <th style={{ width: '3rem' }} />}
        </tr>
      </thead>
      <tbody className="list">
        {columns.map((col) => (
          <ColumnRow
            key={col.key}
            col={col}
            visibilityOptions={visibilityOptions}
            otherColumnLabels={otherColumnLabels}
            onChange={onChange}
            onLabelChange={onLabelChange}
            onDelete={onDelete}
            deletingColumnKey={deletingColumnKey}
            hasDeleteCol={!!onDelete}
          />
        ))}
      </tbody>
    </table>
  </div>
);

export default ColumnVisibilityTable;
